import cloneDeep from 'lodash.clonedeep';
import {
  FormTemplate,
  FormTemplateFieldMap,
  FormTemplateFieldVisibility,
  FormTemplateFieldVisibilityCondition,
  FormTemplateSection,
  FormTemplateVisibilityProperties,
} from './template.types';

type TemplateVisibilityContext = {
  properties?: FormTemplateVisibilityProperties
  features?: string[]
}

export function adaptTemplateFieldVisibility(
  template: FormTemplate,
  context?: TemplateVisibilityContext,
): FormTemplate {
  if (!context || (!context.properties && !context.features)) {
    return template;
  }

  const clonedTemplate = cloneDeep(template);
  Object.values(clonedTemplate).forEach((value) => {
    adaptSectionVisibility(value, context);
  });

  return clonedTemplate;
}

function adaptSectionVisibility(
  maybeSection: unknown,
  context: TemplateVisibilityContext,
): void {
  if (!isTemplateSection(maybeSection)) {
    return;
  }

  applyFieldVisibility(maybeSection.fields, context);

  if (isTemplateSection(maybeSection.general)) {
    adaptSectionVisibility(maybeSection.general, context);
  }
}

function applyFieldVisibility(fields: FormTemplateFieldMap, context: TemplateVisibilityContext): void {
  Object.values(fields).forEach((field) => {
    if (!field.visibility) {
      return;
    }

    field.hide = isFieldVisibleByConditions(field.visibility, context) ? 'false' : 'true';
  });
}

function isFieldVisibleByConditions(
  visibility: FormTemplateFieldVisibility,
  context: TemplateVisibilityContext,
): boolean {
  if (!visibility.conditions || visibility.conditions.length === 0) {
    return true;
  }

  const conditionResults = visibility.conditions.map((condition) => isConditionSatisfied(condition, context));
  return visibility.match === 'any' ? conditionResults.some(Boolean) : conditionResults.every(Boolean);
}

function isConditionSatisfied(
  condition: FormTemplateFieldVisibilityCondition,
  context: TemplateVisibilityContext,
): boolean {
  const operator = condition.operator || 'equals';
  const normalizedExpectedValue = normalizeVisibilityValue(condition.value);

  let actualValue: string | number | boolean | string[] | undefined;
  if (condition.type === 'property') {
    actualValue = context.properties?.[condition.name];
  } else if (condition.type === 'feature') {
    actualValue = (context.features || []).includes(condition.name);
  } else {
    return false;
  }

  const normalizedActualValue = normalizeVisibilityValue(actualValue);

  if (operator === 'equals') {
    return normalizedActualValue === normalizedExpectedValue;
  }

  if (operator === 'notEquals') {
    return normalizedActualValue !== normalizedExpectedValue;
  }

  return false;
}

function normalizeVisibilityValue(value: string | number | boolean | string[] | undefined) {
  if (value === 'true') {
    return true;
  }

  if (value === 'false') {
    return false;
  }

  return value;
}

function isTemplateSection(value: unknown): value is FormTemplateSection {
  if (!value || typeof value !== 'object') {
    return false;
  }

  return Object.prototype.hasOwnProperty.call(value, 'fields');
}
